import { FiClock } from 'react-icons/fi';

export const readingTimeManagementData = {
  courseId: 'time-management-reading',
  courseType: 'reading-course',
  title: 'Reading Time Management',
  icon: FiClock,
  overview: {
    impact: 'Complete all 40 questions in 60 minutes',
    focus: 'Efficient time allocation',
    uses: 'Exam strategy',
    duration: '20 minutes',
  },
  sections: [
    {
      title: 'Time Allocation',
      content: `<p>You have 60 minutes for 3 passages and 40 questions. Allocate time wisely:</p>`,
      table: {
        headers: ['Passage', 'Difficulty', 'Suggested Time'],
        rows: [
          ['Passage 1', 'Easiest', '17 minutes'],
          ['Passage 2', 'Medium', '20 minutes'],
          ['Passage 3', 'Hardest', '23 minutes'],
        ],
      },
      warning: 'Don\'t spend more than 20 minutes on Passage 1! Save time for harder sections.',
    },
    {
      title: 'Within Each Passage',
      content: `<p>Break down your time within each passage:</p>`,
      points: [
        'Skim passage: 2-3 minutes',
        'Read questions: 1-2 minutes',
        'Answer questions: Remaining time',
        'Quick review: Last 30 seconds',
      ],
    },
    {
      title: 'When to Move On',
      content: `<p>Don't get stuck! Know when to skip a question:</p>`,
      points: [
        'If you can\'t find the answer in 90 seconds, mark and move on',
        'Answer easier questions first to secure those points',
        'Return to difficult questions with remaining time',
        'Never leave blanks—guess if you must',
      ],
    },
    {
      title: 'Speed Reading Tips',
      content: `<p>Increase your reading speed without losing comprehension:</p>`,
      points: [
        'Don\'t subvocalize (read aloud in your head)',
        'Use your finger or pen to guide your eyes',
        'Read in chunks, not word by word',
        'Practice reading faster with easy texts first',
        'Build vocabulary to reduce time spent on unknown words',
      ],
    },
  ],
  exercises: [
    {
      title: 'Time Planning',
      type: 'select',
      instructions: 'Choose the best time management decision.',
      questions: [
        {
          text: 'You\'ve spent 2 minutes on a True/False/Not Given question and can\'t find the answer. What should you do?',
          options: ['Mark it and move on', 'Keep looking', 'Guess and move on'],
          answer: 'Mark it and move on',
          explanation: 'Return to it later if you have time. Don\'t waste time.',
        },
        {
          text: 'You finished Passage 1 in 12 minutes. What should you do with the extra 5 minutes?',
          options: ['Save it for Passage 3', 'Re-check Passage 1 answers', 'Take a break'],
          answer: 'Save it for Passage 3',
          explanation: 'Passage 3 is hardest—bank the extra time.',
        },
        {
          text: 'With 5 minutes left, you have 8 questions remaining. What\'s your best strategy?',
          options: ['Answer easy questions, guess the rest', 'Work through systematically', 'Leave some blank'],
          answer: 'Answer easy questions, guess the rest',
          explanation: 'Maximize points on questions you can answer; never leave blanks.',
        },
      ],
    },
    {
      title: 'Time Allocation Strategy',
      type: 'select',
      instructions: 'Choose the best answer about how to divide your 60 minutes across the 3 passages.',
      questions: [
        {
          text: 'Which passage should get the most time allocated?',
          options: ['Passage 1 (easiest)', 'Passage 2 (medium)', 'Passage 3 (hardest)'],
          answer: 'Passage 3 (hardest)',
          explanation: 'Passage 3 is typically the hardest. Allocate around 20–23 minutes so you have time to read carefully and answer all questions.',
        },
        {
          text: 'How should you roughly divide 60 minutes across the 3 passages?',
          options: ['20 minutes each', '15–17 min / 20 min / 23–25 min', '25 min / 20 min / 15 min'],
          answer: '15–17 min / 20 min / 23–25 min',
          explanation: 'Passage 1 is easiest—finish faster. Passage 3 needs more time. A typical split is 17 / 20 / 23 minutes.',
        },
        {
          text: 'How long should you spend on each question type (e.g. matching, T/F/NG)?',
          options: ['Equal time for all types', 'More time on easier types', 'More time on harder types like matching and T/F/NG'],
          answer: 'More time on harder types like matching and T/F/NG',
          explanation: 'Matching headings and T/F/NG often take longer. Budget more time for them and move quickly through simpler question types.',
        },
        {
          text: 'When should you move on from a single question?',
          options: ['After 30 seconds', 'After 90 seconds to 2 minutes', 'Never—keep trying until you find it'],
          answer: 'After 90 seconds to 2 minutes',
          explanation: 'If you cannot find the answer in about 90 seconds, mark it and move on. Return later if time allows.',
        },
      ],
    },
    {
      title: 'Quick Decision Making',
      type: 'select',
      instructions: 'Choose the best decision in each scenario.',
      questions: [
        {
          text: 'You\'ve spent 2.5 minutes on a matching headings question and still can\'t decide. What should you do?',
          options: ['Skip it and move on', 'Keep re-reading the paragraph', 'Guess and move on'],
          answer: 'Skip it and move on',
          explanation: 'Mark it and continue. You can return with remaining time. Don\'t let one question eat into others.',
        },
        {
          text: 'Which question types should you generally attempt first within a passage?',
          options: ['The hardest ones', 'The easiest ones', 'In the order they appear'],
          answer: 'The easiest ones',
          explanation: 'Answer easier questions first to secure points quickly, then tackle harder ones with remaining time.',
        },
        {
          text: 'Which is a time-wasting behavior to avoid?',
          options: ['Skimming the passage before questions', 'Re-reading the entire passage for each question', 'Using keywords to locate answers'],
          answer: 'Re-reading the entire passage for each question',
          explanation: 'Re-reading the whole passage wastes time. Use keywords and skim strategically instead.',
        },
        {
          text: 'You have 3 minutes left and 5 unanswered questions. What should you do?',
          options: ['Leave them blank', 'Guess all 5 quickly', 'Answer 2–3 you can do, guess the rest'],
          answer: 'Answer 2–3 you can do, guess the rest',
          explanation: 'Prioritize questions you can answer, then guess the rest. Never leave blanks—guessing gives you a chance.',
        },
      ],
    },
    {
      title: 'Create Your Time Plan',
      type: 'writing',
      instructions: 'Write a personal time management plan for each section of the IELTS Reading test.',
      questions: [
        {
          text: 'Write your time plan for Section 1 (Passage 1, easiest). Include: skim time, question time, and when to move on.',
          example: 'For Section 1, I will skim the passage in 2 minutes, spend about 12–14 minutes on questions, and move on after 90 seconds on any single question I cannot answer.',
          tip: 'Aim for 15–17 minutes total. Skim 2–3 min, answer questions 12–14 min. Set a clear rule for when to skip.',
        },
        {
          text: 'Write your time plan for Section 2 (Passage 2, medium difficulty). How will you allocate time differently from Section 1?',
          example: 'For Section 2, I will spend 3 minutes skimming, 16–17 minutes on questions, and save 30 seconds for a quick review. I will attempt easier question types first.',
          tip: 'Allow slightly more time than Section 1—around 20 minutes total. Mention which question types you will tackle first.',
        },
        {
          text: 'Write your time plan for Section 3 (Passage 3, hardest). How will you ensure you finish all 40 questions?',
          example: 'For Section 3, I will use 3–4 minutes to skim, then 19–20 minutes on questions. I will mark difficult questions and return to them only if I have time left after answering the rest.',
          tip: 'Budget 23–25 minutes for Section 3. Emphasize marking and returning to hard questions rather than getting stuck.',
        },
      ],
    },
  ],
  tips: [
    'Wear a watch—don\'t rely on room clocks',
    'Practice under timed conditions regularly',
    'Know your pace—how long does each question type take?',
    'Don\'t re-read passages multiple times',
  ],
  dailyPractice: [
    'Time yourself on individual passages (20 min)',
    'Do full 60-minute practice tests weekly',
    'Practice skimming articles in 2-3 minutes',
    'Build speed by reading slightly above your level',
  ],
  backPath: '/exams/english/#reading',
  backLabel: 'Back to Reading',
  aiContext: 'Reading Time Management: Strategies for completing all 40 questions in 60 minutes, including time allocation and speed reading techniques.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...readingTimeManagementData,
      title: readingTimeManagementData.title + ' (Foundations)',
      overview: {
        ...readingTimeManagementData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: readingTimeManagementData.sections.slice(0, Math.max(2, Math.ceil(readingTimeManagementData.sections.length / 2))),
      exercises: readingTimeManagementData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...readingTimeManagementData,
      title: readingTimeManagementData.title + ' (Advanced)',
      overview: {
        ...readingTimeManagementData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: readingTimeManagementData.sections,
      exercises: readingTimeManagementData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(readingTimeManagementData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return readingTimeManagementData;
}
